import React, { useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { MInputTextField, MSelect, MCheckBox } from "src/M_Components";
import DynamicSelectBox from "./DynamicSelectBox";
import "./DynamicForm.css";

/* ===========================================================================================================================
 * DynamicForm
 *
 * This component renders a dynamic form from a backend-driven schema.
 *
 * Supported scenarios:
 * - View mode: renders a read-only reflection of the current entity data.
 * - Create mode: renders editable inputs using defaults and optional value overrides.
 * - Edit mode: renders editable inputs and hydrates server data without overwriting user typing once the form is active.
 *
 * Rendering rules:
 * - Plain scalar fields are rendered using text, number, date, or checkbox inputs.
 * - Foreign keys are rendered with DynamicSelectBox in create/edit and as disabled text in view.
 * - Enums are rendered with MSelect in create/edit and as disabled text in view.
 *
 * Change propagation:
 * - Text, number, and date fields emit lightweight changes on blur.
 * - Selects and checkboxes emit changes on value change.
 * - Foreign key query and option streams emit metadata-only events for parent coordination.
 *
 * Design principles:
 * - Keep the component generic and schema-driven.
 * - Avoid unnecessary parent updates while the user types.
 * - Preserve form responsiveness by validating on blur.
 * - Keep view mode as a pure UI projection of incoming entity data.
 * ===========================================================================================================================
 */
export default function DynamicForm({
  mode = "view",
  schema = [],
  data = {},
  onSubmit = () => { },
  extraSelects = {},
  includeInView = defaultIncludeInView,
  includeInEdit = defaultIncludeInEdit,
  includeInCreate = defaultIncludeInCreate,
  title,
  containerClassName = "alignInputsUserForm",
  align = "auto",
  formRef = null,
  defaultValuesOverride = {},
  getDefaultValue = null,
  onValuesChange = null,
  fkInitialQueries = {},
}) {
  const { t } = useTranslation();

  // #region Mode flags

  /**
   * Cheap mode flags used throughout the component to keep conditional logic readable.
   */
  const isView = mode === "view";
  const isCreate = mode === "create";
  const isEdit = mode === "edit";

  // #endregion

  // #region Default values and form bootstrap

  /**
   * Computes the initial form values.
   *
   * Behavior:
   * - Create mode merges incoming data, explicit overrides, and optional per-field defaults.
   * - Edit and view modes use incoming data directly.
   *
   * The result is memoized to avoid repeating the merge work on unrelated renders.
   */
  const initialDefaults = useMemo(() => {
    const base = { ...(data || {}) };

    if (isCreate) {
      const defaults = { ...base, ...defaultValuesOverride };

      if (typeof getDefaultValue === "function") {
        schema.forEach((opt) => {
          const val = getDefaultValue(opt, { mode, data });

          if (val !== undefined && defaults[opt.Name] === undefined) {
            defaults[opt.Name] = val;
          }
        });
      }

      return defaults;
    }

    return base;
  }, [isCreate, data, schema, defaultValuesOverride, getDefaultValue, mode]);

  /**
   * Initializes react-hook-form.
   *
   * Validation is intentionally configured on blur to keep typing latency low.
   * Field values remain registered even when fields are temporarily unmounted.
   */
  const methods = useForm({
    defaultValues: initialDefaults,
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldFocusError: false,
    shouldUnregister: false,
  });

  const { control, handleSubmit, reset, getValues } = methods;

  // #endregion

  // #region Data synchronization

  /**
   * Synchronizes incoming entity data with the form for the modes that need it.
   *
   * Rules:
   * - View mode always mirrors the latest incoming data.
   * - Edit mode hydrates once when entity data arrives and the form is still effectively empty.
   *
   * The edit-mode emptiness heuristic avoids overwriting user input that was already started.
   */
  useEffect(() => {
    if (isView) {
      reset(data || {});
      return;
    }

    if (isEdit && data && Object.keys(data).length > 0) {
      const current = getValues();
      const isEmpty = Object.values(current).every(
        (v) => v == null || v === ""
      );

      if (isEmpty) {
        reset(data);
      }
    }
  }, [isView, isEdit, data, reset, getValues]);

  // #endregion

  // #region Imperative API

  /**
   * Exposes a minimal imperative API to parent components.
   *
   * Available methods:
   * - submit: triggers the form submit pipeline
   * - getValues: returns the current form value snapshot
   * - reset: replaces form values
   *
   * The ref object is refreshed whenever relevant closures change.
   */
  useEffect(() => {
    if (!formRef) return;

    formRef.current = {
      submit: () => handleSubmit(onSubmit)(),
      getValues: () => getValues(),
      reset: (vals, opts) => reset(vals ?? {}, opts),
    };
  }, [formRef, handleSubmit, onSubmit, reset, getValues]);

  // #endregion

  // #region Change emission

  /**
   * Emits a minimal value patch for one field.
   *
   * This component intentionally avoids sending the full form snapshot for every interaction.
   * Only the changed field is emitted so parent reducers and containers can stay lightweight.
   */
  const handleFieldChange = useCallback(
    (name, cause) => {
      if (typeof onValuesChange !== "function") return;

      const val = getValues(name);
      onValuesChange({ [name]: val }, { cause, name });
    },
    [getValues, onValuesChange]
  );

  /**
   * Emits foreign key search text changes so the parent can persist them per tab or per field.
   *
   * This does not mutate the form value. It only reports remote-search UI state.
   */
  const emitFkQueryChange = useCallback(
    (fieldName, q) => {
      if (typeof onValuesChange !== "function") return;
      onValuesChange({}, { cause: "fk-query", name: fieldName, query: q });
    },
    [onValuesChange]
  );

  /**
   * Emits the current foreign key option page so parent screens can react to the visible results.
   *
   * This is useful for side effects such as map pin alignment or contextual summaries.
   * Form values are not changed by this event.
   */
  const emitFkOptionsChange = useCallback(
    (fieldName, items) => {
      if (typeof onValuesChange !== "function") return;
      onValuesChange({}, { cause: "fk-options", name: fieldName, items });
    },
    [onValuesChange]
  );

  // #endregion

  // #region Schema shaping

  /**
   * Selects the appropriate inclusion rule based on the active mode.
   *
   * The parent can override the default visibility predicates when a screen needs custom behavior.
   */
  const filterByMode = useMemo(() => {
    if (isCreate) return includeInCreate;
    if (isEdit) return includeInEdit;
    return includeInView;
  }, [isCreate, isEdit, includeInCreate, includeInEdit, includeInView]);

  /**
   * Produces the final schema used by the renderer.
   *
   * The schema is:
   * - normalized to an array
   * - filtered by mode
   * - sorted by Order and then Name
   */
  const effectiveSchema = useMemo(() => {
    return asArray(schema).filter(filterByMode).sort(sortByOrderName);
  }, [schema, filterByMode]);

  /**
   * Computes the CSS class applied to the field container.
   *
   * A vertical alignment class can be forced independently from the legacy container class.
   */
  const containerClass = useMemo(() => {
    const forced = align === "vertical" ? "df-align-vertical" : "";
    return [containerClassName, forced].filter(Boolean).join(" ");
  }, [containerClassName, align]);

  // #endregion

  // #region Field rendering helpers

  /**
   * Resolves a stable disabled state for a field.
   *
   * View mode always disables interaction. Outside view mode, the field-level Disabled flag is honored.
   */
  const isFieldDisabled = useCallback(
    (opt) => isView || opt?.Disabled === true,
    [isView]
  );

  /**
   * Maps enum or custom select data into the normalized option shape expected by MSelect.
   *
   * Supported source shapes include:
   * - { value, label }
   * - { Value, Label }
   * - { Id, Name }
   * - primitive values
   */
  const mapSelectOptions = useCallback((items) => {
    return asArray(items).map((e) => ({
      value: e?.value ?? e?.Value ?? e?.Id ?? e,
      label: e?.label ?? e?.Label ?? e?.Name ?? String(e),
    }));
  }, []);

  /**
   * Determines whether a select option list carries numeric values.
   *
   * This is used to coerce select values back to numbers when the schema expects numeric enum values.
   */
  const areAllOptionValuesNumeric = useCallback((options) => {
    if (!Array.isArray(options) || options.length === 0) return false;

    return options.every(
      (o) => o?.value == null || (String(o.value).trim() !== "" && !isNaN(Number(o.value)))
    );
  }, []);

  /**
   * Resolves the text to display for an enum field in view mode.
   *
   * Resolution strategy:
   * - Prefer a string already provided by the server in data[nameKey]
   * - Otherwise map the raw value through the available option list
   * - Fall back to an empty string when nothing can be resolved
   */
  const resolveEnumViewText = useCallback((nameKey, options) => {
    const raw = data?.[nameKey];

    if (typeof raw === "string") {
      return raw;
    }

    const hit = asArray(options).find(
      (o) => String(o.value) === String(raw)
    );

    return hit?.label ?? "";
  }, [data]);

  /**
   * Resolves the text to display for a foreign key field in view mode.
   *
   * Resolution strategy:
   * - Prefer the resolved label already returned by the server under data[nameKey]
   * - Otherwise fall back to the FK id stored under the form key
   */
  const resolveForeignKeyViewText = useCallback((opt) => {
    const nameKey = opt?.Name || "";
    const fkFormKey = getFkFormKey(opt);
    const idVal = data?.[fkFormKey];

    if (data && data[nameKey] != null) {
      return String(data[nameKey]);
    }

    if (idVal != null) {
      return String(idVal);
    }

    return "";
  }, [data]);

  // #endregion

  // #region Main field renderer

  /**
   * Renders one schema-driven field.
   *
   * Supported families:
   * - enums and custom selects
   * - foreign keys
   * - booleans
   * - dates
   * - numbers
   * - strings
   *
   * Each field is wrapped in a Controller so react-hook-form can isolate value updates per field.
   */
  const renderField = (opt) => {
    const nameKey = opt?.Name || "";
    const label = opt?.NormalizedName || nameKey;
    const tooltipMessage = opt?.Description || "";
    const disabled = isFieldDisabled(opt);

    // ---------------------------------------------------------------------------------------------------------------------
    // Enums and custom selects
    //
    // Rendering contract:
    // - View mode: show the resolved label as disabled text
    // - Create/Edit: show a regular MSelect with all options
    //
    // Important details:
    // - Enum payloads may arrive with Label, label, or Name
    // - The stored form value is the option value, not the whole option object
    // - This branch intentionally uses the regular MSelect, not DynamicSelectBox,
    //   because enum lists are local and finite
    // ---------------------------------------------------------------------------------------------------------------------
    if (
      extraSelects?.[nameKey] ||
      (Array.isArray(opt?.EnumType) && opt.EnumType.length > 0)
    ) {
      const extra = extraSelects?.[nameKey];
      const options =
        Array.isArray(extra) && extra.length
          ? mapSelectOptions(extra)
          : mapSelectOptions(opt?.EnumType);

      const optionsAreNumeric = areAllOptionValuesNumeric(options);

      if (isView) {
        const resolved = resolveEnumViewText(nameKey, options);

        return (
          <MInputTextField
            key={nameKey}
            label={t(label)}
            tooltipMessage={tooltipMessage}
            value={resolved}
            disabled
          />
        );
      }

      return (
        <Controller
          key={nameKey}
          name={nameKey}
          control={control}
          render={({ field }) => (
            <MSelect
              label={t(label)}
              options={options}
              value={field.value ?? ""}
              onChange={(e) => {
                const raw = e?.target?.value;
                const next =
                  raw === "" || raw == null
                    ? null
                    : optionsAreNumeric
                      ? Number(raw)
                      : raw;

                field.onChange(next);
                handleFieldChange(nameKey, "change");
              }}
              required={!!opt.IsRequired}
              disabled={disabled}
              noNone
            />
          )}
        />
      );
    }

    // ---------------------------------------------------------------------------------------------------------------------
    // Foreign keys
    //
    // Rendering contract:
    // - View mode: show the resolved label as disabled text
    // - Create/Edit: show DynamicSelectBox backed by remote paging and filtering
    //
    // Important details:
    // - The form key for a foreign key can differ from the display name
    // - The initial query can be seeded from persisted parent state or from the resolved label
    // - The actual stored value is the numeric foreign key id
    // ---------------------------------------------------------------------------------------------------------------------
    if (opt?.IsForeignKey && opt?.ForeignTableName) {
      const fkFormKey = getFkFormKey(opt);
      const initialQuerySeed =
        (data && data[nameKey] != null ? String(data[nameKey]) : "") || "";

      if (isView) {
        const resolved = resolveForeignKeyViewText(opt);

        return (
          <MInputTextField
            key={nameKey}
            label={t(label)}
            tooltipMessage={tooltipMessage}
            value={resolved}
            disabled
          />
        );
      }

      return (
        <Controller
          key={nameKey}
          name={fkFormKey}
          control={control}
          render={({ field }) => (
            <DynamicSelectBox
              label={t(label)}
              tableName={opt.ForeignTableName}
              groupBy={opt.GroupByInfo || "Id"}
              valueId={
                field.value == null ||
                  field.value === "" ||
                  Number(field.value) === 0
                  ? null
                  : Number(field.value)
              }
              required={!!opt.IsRequired}
              disabled={disabled}
              initialQuery={
                isEdit || isCreate
                  ? fkInitialQueries?.[fkFormKey] ?? initialQuerySeed ?? ""
                  : ""
              }
              onQueryChange={(q) => emitFkQueryChange(fkFormKey, q)}
              onOptionsChange={(rows) => emitFkOptionsChange(fkFormKey, rows)}
              onChange={(sel) => {
                const next = sel ? Number(sel.value) : null;
                field.onChange(next);
                handleFieldChange(fkFormKey, "change");
              }}
            />
          )}
        />
      );
    }

    // ---------------------------------------------------------------------------------------------------------------------
    // Booleans
    //
    // Rendering contract:
    // - Always represented as a checkbox
    // - Emits on change because the interaction is discrete
    // - Also emits on blur to keep the event model consistent with the rest of the form
    // ---------------------------------------------------------------------------------------------------------------------
    if (isBool(opt)) {
      return (
        <Controller
          key={nameKey}
          name={nameKey}
          control={control}
          render={({ field }) => (
            <MCheckBox
              label={t(label)}
              checked={Boolean(field.value)}
              onChange={(e) => {
                field.onChange(!!e?.target?.checked);
                handleFieldChange(nameKey, "change");
              }}
              onBlur={() => handleFieldChange(nameKey, "blur")}
              disabled={disabled}
            />
          )}
        />
      );
    }

    // ---------------------------------------------------------------------------------------------------------------------
    // Dates
    //
    // Rendering contract:
    // - Uses a date input
    // - Normalizes incoming values to yyyy-mm-dd
    // - Emits only on blur to avoid frequent change propagation while a date is still being typed
    // ---------------------------------------------------------------------------------------------------------------------
    if (isDateish(opt)) {
      return (
        <Controller
          key={nameKey}
          name={nameKey}
          control={control}
          render={({ field }) => (
            <MInputTextField
              label={t(label)}
              tooltipMessage={tooltipMessage}
              type="date"
              value={asInputDate(field.value)}
              onChange={(e) => {
                field.onChange(e?.target?.value ?? "");
              }}
              onBlur={() => handleFieldChange(nameKey, "blur")}
              required={!!opt.IsRequired}
              disabled={disabled}
            />
          )}
        />
      );
    }

    // ---------------------------------------------------------------------------------------------------------------------
    // Numbers
    //
    // Rendering contract:
    // - Keeps the raw input string while the user types
    // - Emits only on blur
    //
    // This avoids fighting with partial numeric input such as a temporary minus sign or decimal typing flow.
    // ---------------------------------------------------------------------------------------------------------------------
    if (isNumber(opt)) {
      return (
        <Controller
          key={nameKey}
          name={nameKey}
          control={control}
          render={({ field }) => (
            <MInputTextField
              label={t(label)}
              tooltipMessage={tooltipMessage}
              type="number"
              value={field.value ?? ""}
              onChange={(e) => {
                field.onChange(e?.target?.value ?? "");
              }}
              onBlur={() => handleFieldChange(nameKey, "blur")}
              required={!!opt.IsRequired}
              disabled={disabled}
            />
          )}
        />
      );
    }

    // ---------------------------------------------------------------------------------------------------------------------
    // Strings
    //
    // Rendering contract:
    // - Uses the default text input
    // - Emits only on blur
    //
    // This keeps typing responsive and prevents parent update storms in large forms.
    // ---------------------------------------------------------------------------------------------------------------------
    return (
      <Controller
        key={nameKey}
        name={nameKey}
        control={control}
        render={({ field }) => (
          <MInputTextField
            label={t(label)}
            tooltipMessage={tooltipMessage}
            value={field.value ?? ""}
            onChange={(e) => {
              field.onChange(e?.target?.value ?? "");
            }}
            onBlur={() => handleFieldChange(nameKey, "blur")}
            required={!!opt.IsRequired}
            disabled={disabled}
          />
        )}
      />
    );
  };

  // #endregion

  // #region Render

  /**
   * Renders the form shell and the schema-driven field list.
   *
   * FormProvider is used so nested children can access react-hook-form context when needed.
   */
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="dynamic-form-root">
        <div className={containerClass}>
          {title && <span className="dynamic-form-title">{t(title)}</span>}

          {effectiveSchema.map((opt, idx) => (
            <div key={`df-fld-${opt?.Name || idx}`}>{renderField(opt)}</div>
          ))}
        </div>
      </form>
    </FormProvider>
  );

  // #endregion
}

/* ===========================================================================================================================
 * Default inclusion rules
 *
 * These predicates define which schema fields are shown in each form mode.
 * Parent screens can override them when a specific form needs different visibility rules.
 * ===========================================================================================================================
 */

// #region Inclusion rules

/**
 * Default field visibility rule for create mode.
 *
 * Hidden in create:
 * - keys
 * - audit fields
 * - fields marked as hidden in edit
 * - the Inactive field
 */
const defaultIncludeInCreate = (opt) => {
  if (!opt) return false;
  if (opt.IsKey || isAudit(opt.Name)) return false;
  if (opt.IsHiddenEdit === true) return false;
  if (opt.Name === "Inactive") return false;
  return true;
};

/**
 * Default field visibility rule for edit mode.
 *
 * Hidden in edit:
 * - keys
 * - audit fields
 * - fields marked as hidden in edit
 */
const defaultIncludeInEdit = (opt) => {
  if (!opt) return false;
  if (opt.IsKey || isAudit(opt.Name)) return false;
  if (opt.IsHiddenEdit === true) return false;
  return true;
};

/**
 * Default field visibility rule for view mode.
 *
 * Hidden in view:
 * - keys
 * - audit fields
 * - fields marked as hidden in list
 */
const defaultIncludeInView = (opt) => {
  if (!opt) return false;
  if (opt.IsKey || isAudit(opt.Name)) return false;
  if (opt.IsHiddenList === true) return false;
  return true;
};

// #endregion

/* ===========================================================================================================================
 * Helper functions
 *
 * These helpers keep the main renderer focused and make schema interpretation easier to follow.
 * ===========================================================================================================================
 */

// #region Type and schema helpers

/**
 * Detects whether a field name belongs to the audit group.
 */
const isAudit = (name) =>
  ["CreatedAt", "UpdatedAt", "CreatedBy", "UpdatedBy"].includes(name);

/**
 * Detects boolean schema fields.
 */
const isBool = (opt) => opt?.Type === "boolean" || opt?.Type === "bool";

/**
 * Detects numeric schema fields.
 */
const isNumber = (opt) =>
  ["int", "int32", "decimal", "double"].includes(opt?.Type);

/**
 * Detects date-like schema fields.
 */
const isDateish = (opt) =>
  ["date", "datetime", "Date", "DateTime"].includes(opt?.Type);

/**
 * Safely normalizes a value into an array.
 */
const asArray = (v) => (Array.isArray(v) ? v : []);

/**
 * Provides stable schema ordering using Order first and Name second.
 *
 * This keeps layout deterministic even when multiple fields share the same order value.
 */
const sortByOrderName = (a, b) => {
  const ao = a?.Order ?? 0;
  const bo = b?.Order ?? 0;

  if (ao !== bo) return ao - bo;

  return String(a?.Name || "").localeCompare(String(b?.Name || ""));
};

/**
 * Normalizes a date-like input to yyyy-mm-dd for HTML date inputs.
 *
 * Accepted sources:
 * - already normalized strings
 * - ISO strings
 * - values that can be parsed by Date
 */
const asInputDate = (d) => {
  if (!d) return "";

  try {
    const s = String(d);

    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      return s.slice(0, 10);
    }

    const dd = new Date(s);

    if (!isNaN(dd.getTime())) {
      return dd.toISOString().slice(0, 10);
    }
  } catch {
    return "";
  }

  return "";
};

/**
 * Resolves the canonical form key for a foreign key field.
 *
 * Rules:
 * - If Header exists, it is used because many schemas expose the FK storage name there
 * - Otherwise the Name is used and forced to end with Id
 */
const getFkFormKey = (opt) => {
  const nameKey = opt?.Name || "";

  if (opt?.Header) return opt.Header;

  return nameKey?.endsWith("Id") ? nameKey : `${nameKey}Id`;
};

// #endregion

// #region PropTypes

DynamicForm.propTypes = {
  mode: PropTypes.oneOf(["create", "edit", "view"]),
  schema: PropTypes.array.isRequired,
  data: PropTypes.object,
  onSubmit: PropTypes.func,
  extraSelects: PropTypes.object,
  includeInView: PropTypes.func,
  includeInEdit: PropTypes.func,
  includeInCreate: PropTypes.func,
  title: PropTypes.string,
  containerClassName: PropTypes.string,
  align: PropTypes.oneOf(["auto", "vertical"]),
  formRef: PropTypes.any,
  defaultValuesOverride: PropTypes.object,
  getDefaultValue: PropTypes.func,
  onValuesChange: PropTypes.func,
  fkInitialQueries: PropTypes.object,
};

// #endregion